import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  faCheckCircle, 
  faTimesCircle, 
  faQuestionCircle,
  faListAlt,
  faClock
} from '@fortawesome/free-solid-svg-icons';

// Contexts
import { ThemeContext } from "../../context/ThemeProvider";
import { AuthContext } from "../../context/AuthProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import OnboardingLayout from "./OnboardingLayout";
import StatsGrid from "../../components/common/StatsGrid";
import { TestReviewScore, TestReviewDomains } from "../../components/testReview";
import { TestReviewCommunity } from "../../components/common/CommonModals";

// API & Constants
import { GET_COMMUNITY_MULTIPLE_QUESTION_DETAILS } from "../../api/community";
import { GET_APP_DOMAIN_LIST } from '../../../app_constant';
import { STRING } from "../../constant";

const OnboardingTestReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const { isVisibleCommunity } = useContext(AuthContext);
  const { trackAssessmentComplete, trackOnboardingComplete } = useAnalytics();
  
  const testData = location.state?.historyData || JSON.parse(localStorage.getItem('onboarding_test_result'))?.testData;
  const certificate_id = location.state?.selected_exam?.key || JSON.parse(localStorage.getItem('onboarding_test_selected_exam'))?.key;
  const exam_name = location.state?.selected_exam?.name || JSON.parse(localStorage.getItem('onboarding_test_selected_exam'))?.name;
  
  
  // State management
  const [isLoadingCommunity, setLoadingCommunity] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [communityDetails, setCommunityDetails] = useState(null);
  const [communityPercentage, setCommunityPercentage] = useState(0);
  const [domainList, setDomainList] = useState([]);

  // Initialize data
  useEffect(() => {
    getScoreAsPerDomain();
    if (testData?.test_questions_meta_data && isVisibleCommunity) {
      const questionIds = testData.test_questions_meta_data.map(e => `${e.question_id}`);
      getCommunityDetails(questionIds);
    }
    
    // Track assessment completion
    if (testData) {
      trackAssessmentComplete(testData.score, {
        questions_answered: testData.total,
        correct_answers: testData.correct,
        exam_type: certificate_id
      });
      
      // Track onboarding completion
      trackOnboardingComplete({
        completed_assessment: true,
        initial_score: testData.score,
        exam_type: certificate_id
      });
    }
  }, []);

  // Get community details
  async function getCommunityDetails(ids) {
    setLoadingCommunity(true);
    try {
      const result = await GET_COMMUNITY_MULTIPLE_QUESTION_DETAILS(ids);
      setLoadingCommunity(false);

      if (result?.data?.length > 0) {
        const totalAnswered = result.data
          .map(item => item.times_answered)
          .reduce((prev, next) => prev + next);
        const totalCorrect = result.data
          .map(item => item.times_answered_correct)
          .reduce((prev, next) => prev + next);

        setCommunityDetails(result.data);
        const percentage = (totalCorrect / totalAnswered) * 100;
        setCommunityPercentage(percentage);
      } else {
        setCommunityDetails([]);
        setCommunityPercentage(0);
      }
    } catch (error) {
      setLoadingCommunity(false);
      setCommunityDetails([]);
      setCommunityPercentage(0);
    }
  }

  // Calculate domain scores
  function getScoreAsPerDomain() {
    const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(certificate_id);
    const domainScoreList = [];

    APP_DOMAIN_LIST.forEach((domain) => {
      const totalQuestions = (testData?.test_questions_meta_data ?? []).filter(
        e => e.topic_id === domain.id
      );
      const correctQuestions = totalQuestions.filter(
        e => e.response === STRING.correct_tag
      );
      
      const percentage = totalQuestions.length > 0
        ? ((100 * correctQuestions.length) / totalQuestions.length).toFixed(2)
        : 0;

      if (totalQuestions.length > 0) {
        domainScoreList.push({
          id: domain.id,
          title: domain.domain_name,
          value: `${percentage}%`,
          total: totalQuestions.length,
          percentage: parseFloat(percentage),
          correct: correctQuestions.length,
        });
      }
    });

    setDomainList(domainScoreList);
  }

  // Get test statistics
  function getTestStats() {
    const score = testData?.score || 0;
    const timeTaken = testData?.time_taken || 0;
    const totalQuestions = testData?.total || 0;
    const avgTime = totalQuestions > 0 ? Math.floor(timeTaken / totalQuestions) : 0;

    return {
      score,
      avgTime: `${avgTime}s`,
      totalQuestions,
      correct: testData?.correct || 0,
      incorrect: testData?.incorrect || 0,
      unanswered: testData?.unanswered || 0,
    };
  }

  // Navigation handlers
  function handleQuestionReview(type, questions, title) {
    if (questions.length > 0) {
      console.log("certificate_id", certificate_id);
      navigate("/onboarding-question-review", {
        state: {
          questions: questions,
          title: title,
          testData: testData,
          selectedCertificate: certificate_id
        }
      });
    }
  }

  function handleAllQuestions() {
    handleQuestionReview(
      'all',
      testData?.test_questions_meta_data || [],
      'All Questions'
    );
  }

  function handleUnansweredQuestions() {
    const questions = testData?.test_questions_meta_data?.filter(
      item => item.response === STRING.unanswered_tag
    ) || [];
    handleQuestionReview('unanswered', questions, 'Unanswered');
  }

  function handleCorrectQuestions() {
    const questions = testData?.test_questions_meta_data?.filter(
      item => item.response === STRING.correct_tag
    ) || [];
    handleQuestionReview('correct', questions, 'Correct');
  }

  function handleIncorrectQuestions() {
    const questions = testData?.test_questions_meta_data?.filter(
      item => item.response === STRING.incorrect_tag
    ) || [];
    handleQuestionReview('incorrect', questions, 'Incorrect');
  }

  function handleDomainQuestions(domain) {
    const questions = testData?.test_questions_meta_data?.filter(
      e => e.topic_id === domain.id
    ) || [];
    handleQuestionReview('domain', questions, domain.title);
  }

  function getPerformanceMessage(score) {
    console.log("location.state?.selected_exam", location.state?.selected_exam);
    if (score <= 30) {
      return {
        title: "Don't worry - everyone starts somewhere!",
        text: `Your diagnostic results show you have significant room for growth in ${exam_name} concepts. This is actually good news - it means our personalized study plan will have the maximum impact on your success.`,
        cta: `Start Your ${exam_name} Journey`
      };
    } else if (score <= 60) {
      return {
        title: "Good foundation, let's build on it!",
        text: `You have some ${exam_name} knowledge, but there are key gaps that could impact your exam success. Our targeted study plan will help you strengthen weak areas and build confidence.`,
        cta: "Complete Your Preparation"
      };
    } else if (score <= 80) {
      return {
        title: "Strong performance - you're almost there!",
        text: `You have solid ${exam_name} knowledge! With focused practice on your weaker domains and some exam strategy refinement, you'll be ready to pass with confidence.`,
        cta: "Perfect Your Skills"
      };
    } else {
      return {
        title: "Excellent! You're exam-ready!",
        text: `Outstanding performance! You have strong ${exam_name} knowledge across all domains. Use our advanced practice tests and final review materials to maintain your edge.`,
        cta: "Fine-Tune for Success"
      };
    }
  }

  function handleContinueToHome() {
    navigate("/home");
  }

  const stats = getTestStats();
  const testName = testData?.test_name?.trim() || "Assessment Result";
  const testDate = testData?.date?.trim() || new Date().toLocaleDateString();

  const calculateStats = () => {
    const statsData = [
      {
        icon: faListAlt,
        stat: stats.totalQuestions,
        label: 'Total Questions',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome',
        ...(stats.totalQuestions > 0 && {
          link: 'Review',
          onLinkClick: handleAllQuestions
        })
      },
      
      {
        icon: faCheckCircle,
        stat: `${stats.correct}`,
        label: 'Total Correct',
        color: theme?.primary?.green?.[500],
        iconType: 'fontawesome',
        ...(stats.correct > 0 && {
          link: 'Review',
          onLinkClick: handleCorrectQuestions
        })
      },
      {
        icon: faTimesCircle,
        stat: `${stats.incorrect}`,
        label: 'Total Incorrect',
        color: theme?.primary?.red?.[500],
        iconType: 'fontawesome',
        ...(stats.incorrect > 0 && {
          link: 'Review',
          onLinkClick: handleIncorrectQuestions
        })
      },
      {
        icon: faQuestionCircle,
        stat: `${stats.unanswered}`,
        label: 'Total Unanswered',
        color: theme?.primary?.yellow?.[500],
        iconType: 'fontawesome',
        ...(stats.unanswered > 0 && {
          link: 'Review',
          onLinkClick: handleUnansweredQuestions
        })
      }
    ];
    return statsData;
  }

  return (
    <OnboardingLayout>
      <div className="min-h-screen transition-colors duration-300" 
           style={{ background: theme?.background?.main }}>
        <div className="mx-4 sm:mx-4 md:mx-8 lg:mx-8 mb-4 sm:mb-4 md:mb-8 lg:mb-8 mt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2" style={{ color: theme?.text?.primary }}>
                {testName}
              </h1>
              <p className="text-lg" style={{ color: theme?.text?.secondary }}>
                Review your assessment results and track your progress
              </p>
            </div>
            
            <StatsGrid
              stats={calculateStats()}
              theme={theme}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
              <div className="flex flex-col items-center">
                <div className="w-full">
                  <TestReviewScore
                    score={stats.score}
                    avgTime={stats.avgTime}
                    theme={theme}
                    showCommunityIcon={isVisibleCommunity}
                    onCommunityClick={() => setShowCommunityModal(true)}
                  />
                </div>
                
                <div className="flex flex-col items-center mt-20 space-y-4 w-full">
                  {(() => {
                    const performanceMessage = getPerformanceMessage(stats.score);
                    return (
                      <>
                        <div className="text-center max-w-2xl">
                          <h3 className="text-xl font-semibold mb-3" style={{ color: theme?.text?.primary }}>
                            {performanceMessage.title}
                          </h3>
                          <p className="text-base leading-relaxed" style={{ color: theme?.text?.secondary }}>
                            {performanceMessage.text}
                          </p>
                        </div>
                        <button
                          onClick={handleContinueToHome}
                          className="px-8 mt-4 py-4 text-white w-[90%] rounded-lg hover:scale-105 transition-all duration-200 font-medium shadow-lg"
                          style={{
                            background: theme?.primary?.blue?.[500],
                            color: theme?.neutral?.absoluteWhite,
                          }}
                        >
                          {performanceMessage.cta}
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div>
                {/* Domain Scores */}
                <TestReviewDomains
                  domains={domainList}
                  onDomainClick={handleDomainQuestions}
                  theme={theme}
                />
              </div>
            </div>

            
          </motion.div>
        </div>

        {/* Community Modal */}
        <TestReviewCommunity
          isOpen={showCommunityModal}
          type="Assessment"
          onClose={() => setShowCommunityModal(false)}
          communityStats={{
            color: theme?.dashboard?.status?.correct || '#10b981',
            percentage: communityPercentage,
            message: "Community performance data",
            level: 'easy'
          }}
          theme={theme}
        />
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingTestReview;